import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Divider, Form, Input, Sidebar, TextArea } from 'semantic-ui-react';
import { issueApprove } from './issues-slice';
import { documentApprove } from '../document/documents-slice';
import { resendNewSecurityCode, signatoryEmailReceived } from '../auth/auth-slice';
import RejectSignatureSuccess from './RejectSignatureSuccess';
import { SignatureFailed } from './SignatureFailed';
import ApproveSignatureSuccess from './ApproveSignatureSuccess';
import { useIntl, FormattedMessage } from 'react-intl';
import CloseIcon from './CloseIcon';
import { useNavigate } from 'react-router-dom';

const prefix = 'signatureSideBar';

function Required() {
  return <span className="required">{'*'}</span>;
}

export default function SignatureSideBar(props) {
  const signatureReason = props.signatureReason;
  const signatureType = props.signatureType;
  const approve = props.approve;
  const issueType = props.stageName;
  const requiresComment = props.requiresComment;
  const approveCbk = props.approveCbk;
  const onClose = props.onClose;
  const itemType = props.itemType;
  const visible = props.visible;
  const [hasValidationError, setHasValidationError] = useState(false);
  const [role, setRole] = useState('');
  const [securityCode, setSecurityCode] = useState('');
  const [comment, setComment] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [securityCodeResent, setSecurityCodeResent] = useState(false);
  const showSuccessModal = props.showSuccessModal;
  const setShowSuccessModal = props.setShowSuccessModal;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const intl = useIntl();

  const { signatoryEmail, itemAssignmentCode } = useSelector((state) => ({
    signatoryEmail: state.auth.session?.signatoryEmail,
    itemAssignmentCode: state.auth.user.itemAssignmentCode,
  }));
  const { issueSigned, issueApprovalError, signedType } = useSelector((state) => ({
    issueSigned: itemType === 'document' ? state.documents.document.signed : state.issues.issue.signed,
    issueApprovalError:
      itemType === 'document' ? state.documents.document.approvalError : state.issues.issue.approvalError,
    signedType: itemType === 'document' ? state.documents.document.signedType : state.issues.issue.signedType,
    state: state,
  }));

  useEffect(() => {
    if (
      issueApprovalError === 'invalidRequest' ||
      issueApprovalError === 'itemNotFound' ||
      issueApprovalError === 'somethingWentWrong'
    ) {
      setErrorMessage(
        intl.formatMessage({ id: `errors.${issueApprovalError}`, defaultMessage: 'Something went wrong' })
      );
    }
  }, [issueApprovalError]);

  const setSignatoryEmail = (email) => {
    dispatch(signatoryEmailReceived(email));
  };

  const handleApproval = () => {
    if (
      securityCode.trim() === '' ||
      signatoryEmail === undefined ||
      signatoryEmail?.trim() === '' ||
      role.trim() === ''
    ) {
      setHasValidationError(true);
      return;
    }

    if (isRequired() && comment.trim() === '') {
      setHasValidationError(true);
      return;
    }

    approveCbk
      ? approveCbk()
      : itemType === 'document'
      ? dispatch(
          documentApprove({
            itemAssignmentCode,
            signatoryEmail,
            role,
            securityCode,
            comment,
            isApproved: approve,
            navigate,
          })
        )
          .unwrap()
          .then(() => {
            setShowSuccessModal(true);
            onClose();
          })
          .catch(() => {})
      : dispatch(
          issueApprove({
            itemAssignmentCode,
            signatoryEmail,
            role,
            securityCode,
            comment,
            isApproved: approve,
            navigate,
          })
        )
          .unwrap()
          .then(() => {
            setShowSuccessModal(true);
            onClose();
          })
          .catch(() => {});
  };

  const handleResend = (event) => {
    if (securityCodeResent) {
      event.preventDefault();
      return;
    }
    dispatch(resendNewSecurityCode({ code: itemAssignmentCode, type: itemType, navigate: navigate }));
    setSecurityCodeResent(true);
    setTimeout(() => {
      setSecurityCodeResent(false);
    }, 3000);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const securityCodeErrors = () => {
    if (hasValidationError && securityCode === '') {
      return intl.formatMessage({ id: `${prefix}.securityCodeRequired`, defaultMessage: 'Security code is required' });
    }
    if (issueApprovalError === 'invalidSecurityCode' || issueApprovalError === 'expiredSecurityCode') {
      return intl.formatMessage({
        id: `errors.${issueApprovalError}`,
        defaultMessage: 'Invalid/Expired Security Code',
      });
    }
    return '';
  };

  const emailErrors = () => {
    if (issueApprovalError === 'unknownEmail') {
      return intl.formatMessage({ id: `errors.${issueApprovalError}`, defaultMessage: 'Invalid Email' });
    }
    if (hasValidationError && (signatoryEmail === undefined || signatoryEmail === '')) {
      return intl.formatMessage({ id: `${prefix}.emailRequired`, defaultMessage: 'Email is required' });
    }
    return '';
  };

  const issueApproved = () => {
    return approve && issueSigned && issueApprovalError === '' && signedType === 'approved';
  };

  function issueRejected() {
    return !approve && issueSigned && issueApprovalError === '' && signedType === 'rejected';
  }

  const translatedSignatureType = intl.formatMessage({
    id: `signatureType.${signatureType}`,
    defaultMessage: `${signatureType}`,
  });

  const translatedIssueType = intl.formatMessage({
    id: `issueType.${itemType === 'document' ? itemType : issueType}`,
    defaultMessage: `${issueType}`,
  });

  function isRequired() {
    return requiresComment || !approve;
  }

  return (
    <div className={`signature-side-bar ${approve ? 'approve-modal' : 'reject-modal'}`} data-testid="side-bar">
      <Sidebar animation="overlay" direction="right" width="very wide" visible={visible} className="side-bar">
        <CloseIcon handleClose={onClose} />
        <Form className="signature-form">
          <div className="header">
            <p>
              {' '}
              {translatedSignatureType} {translatedIssueType}
            </p>
          </div>
          <div>
            {' '}
            <FormattedMessage
              id={`${prefix}.enterInfo`}
              defaultMessage={`Please enter the email and security code that was used to access this ${translatedIssueType}.`}
              values={{ translatedIssueType }}
            />
          </div>
          <Divider hidden />
          <div className="sub-header">
            <FormattedMessage id={`${prefix}.signatureReason`} defaultMessage="Signature Reason" />
          </div>
          <div>
            <p>
              {itemType === 'document' && signatureType === 'reject' ? (
                <FormattedMessage id={`${prefix}.reject_signature_reason`} defaultMessage="Signature Reason" />
              ) : (
                signatureReason
              )}
            </p>
          </div>
          <Divider hidden />
          <div className="sub-header">
            <FormattedMessage id={`${prefix}.email`} defaultMessage="Email" />
            <Required />
          </div>
          <Input
            name="email"
            fluid={true}
            placeholder={intl.formatMessage({ id: `${prefix}.email`, defaultMessage: 'Email' })}
            value={signatoryEmail}
            onChange={(e) => setSignatoryEmail(e.target.value)}
            className={hasValidationError && (signatoryEmail === undefined || signatoryEmail === '') ? 'error' : ''}
          />
          {emailErrors() !== '' && <div className="ui pointing basic label form-error-message"> {emailErrors()}</div>}
          <Divider hidden />
          <div className="sub-header">
            <FormattedMessage id={`${prefix}.title`} defaultMessage="Title" />
            <Required />
          </div>
          <Input
            name="title"
            fluid={true}
            placeholder={intl.formatMessage({ id: `${prefix}.title`, defaultMessage: 'Title' })}
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className={hasValidationError && role === '' ? 'error' : ''}
          />
          {hasValidationError && role === '' && (
            <div className="ui pointing basic label form-error-message">
              <FormattedMessage id={`${prefix}.roleRequired`} defaultMessage="Role is required" />
            </div>
          )}
          <Divider hidden> </Divider>
          <div className="sub-header">
            <FormattedMessage id={`${prefix}.securityCode`} defaultMessage="Security Code" />
            <Required />
          </div>
          <Input
            name="securityCode"
            fluid={true}
            placeholder={intl.formatMessage({ id: `${prefix}.securityCode`, defaultMessage: 'Security Code' })}
            value={securityCode}
            onChange={(e) => setSecurityCode(e.target.value)}
            className={securityCodeErrors() !== '' ? 'error' : ''}
          />
          {securityCodeErrors() !== '' && (
            <div className="ui pointing basic label form-error-message"> {securityCodeErrors()}</div>
          )}
          <div className="custom-modal-link">
            {securityCodeResent ? (
              <div className="ui pointing label">
                <FormattedMessage
                  id={`${prefix}.resentSecurityCode`}
                  defaultMessage="Security code is resent to you email"
                />
              </div>
            ) : (
              <a href="#" onClick={handleResend}>
                <FormattedMessage id={`${prefix}.resendCode`} defaultMessage="Resend code" />
              </a>
            )}
          </div>
          <div className="sub-header">
            <FormattedMessage id={`${prefix}.comment`} defaultMessage="Comment" />
            {isRequired() && <Required />}
          </div>
          <TextArea
            name="comment"
            fluid={true.toString()}
            placeholder={intl.formatMessage({ id: `${prefix}.comment`, defaultMessage: 'Comment' })}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <div className="required">
            <FormattedMessage
              id={`${prefix}.commentAuditMessage`}
              defaultMessage="Your comment will be logged in the Audit Trail"
            />
          </div>
          {isRequired() && hasValidationError && comment === '' && (
            <div className="ui pointing basic label form-error-message">
              <FormattedMessage id={`${prefix}.commentRequired`} defaultMessage="Comment is required" />
            </div>
          )}
          <Divider hidden> </Divider>
          <div className="button-container">
            <Button
              data-testid="submit-electronic-signature"
              className={
                approve
                  ? 'ui green button approve-submit-electronic-signature'
                  : 'ui red button reject-submit-electronic-signature'
              }
              onClick={handleApproval}
            >
              <FormattedMessage id={`${prefix}.submitSignature`} defaultMessage="Submit Electronic Signature" />
            </Button>
            <Button className="cancel-electronic-signature" onClick={onClose}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
          </div>
        </Form>
      </Sidebar>
      {
        <>
          {issueApproved() && showSuccessModal && (
            <ApproveSignatureSuccess
              type={issueType}
              itemType={itemType}
              handleSidebarClose={handleCloseSuccessModal}
            ></ApproveSignatureSuccess>
          )}
          {issueRejected() && showSuccessModal && (
            <RejectSignatureSuccess
              type={issueType}
              itemType={itemType}
              handleSidebarClose={handleCloseSuccessModal}
            ></RejectSignatureSuccess>
          )}
          {errorMessage !== '' && (
            <SignatureFailed errorMessage={errorMessage} handleSidebarClose={onClose}></SignatureFailed>
          )}
        </>
      }
    </div>
  );
}
